import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Spinner, EmptyState } from '@/shared/ui';
import { useAuthStore } from '@/store/authStore';
import { useSetupGuideStore } from '@/store/setupGuideStore';
import { useGuideContext, useMarkGuideStep } from './useGuide';
import { employerGuide } from './guideContent.employer';
import { employeeGuide } from './guideContent.employee';
import { employerChapters, employeeChapters } from './chapters';
import { computeProgress } from './progress';
import { GuideSectionView } from './GuideSectionView';
import type { DocItem } from './ContextCards';
import styles from './SetupGuide.module.css';

const EMPLOYER_DOCS: DocItem[] = [
  { id: 'lohnausweis', icon: 'fa-file-invoice', titleKey: 'guide.employer.documents.lohnausweis.title', bodyKey: 'guide.employer.documents.lohnausweis.body', href: '/taxes', ctaKey: 'guide.employer.documents.lohnausweis.cta' },
  { id: 'withholding', icon: 'fa-file-export', titleKey: 'guide.employer.documents.withholding.title', bodyKey: 'guide.employer.documents.withholding.body', href: '/taxes', ctaKey: 'guide.employer.documents.withholding.cta' },
  { id: 'contract', icon: 'fa-file-signature', titleKey: 'guide.employer.documents.contract.title', bodyKey: 'guide.employer.documents.contract.body', href: '/settings/contracts', ctaKey: 'guide.employer.documents.contract.cta' },
  { id: 'checklist', icon: 'fa-list-check', titleKey: 'guide.employer.documents.checklist.title', bodyKey: 'guide.employer.documents.checklist.body', href: '/resources/registration-checklist', ctaKey: 'guide.ui.open' },
];

const EMPLOYEE_DOCS: DocItem[] = [
  { id: 'rightsGuide', icon: 'fa-book-open', titleKey: 'guide.employee.rights.docGuide.title', bodyKey: 'guide.employee.rights.docGuide.body', href: '/resources/rights-guide', ctaKey: 'guide.ui.open' },
  { id: 'contractCheck', icon: 'fa-clipboard-check', titleKey: 'guide.employee.rights.docContract.title', bodyKey: 'guide.employee.rights.docContract.body', href: '/resources/contract-checklist', ctaKey: 'guide.ui.open' },
];

/**
 * The Setup Guide, presented as a short multi-step wizard (3–4 chapters) so a new user
 * isn't faced with the whole guide at once. Opened from the sidebar menu and the
 * dashboard banner via the setup-guide store.
 */
export function SetupGuideModal() {
  const { t } = useTranslation();
  const open = useSetupGuideStore((s) => s.open);
  const closeGuide = useSetupGuideStore((s) => s.closeGuide);
  const role = useAuthStore((s) => s.role);
  const guideRole: 'employer' | 'employee' = role === 'employee' ? 'employee' : 'employer';

  const { data, isLoading, isError } = useGuideContext(guideRole);
  const markStep = useMarkGuideStep(guideRole);

  const def = guideRole === 'employee' ? employeeGuide : employerGuide;
  const chapters = guideRole === 'employee' ? employeeChapters : employerChapters;
  const ctx = data?.context;

  const [stepIndex, setStepIndex] = useState(0);
  // Restart at the first chapter each time the guide is opened.
  useEffect(() => {
    if (open) setStepIndex(0);
  }, [open]);

  const completedSet = useMemo(
    () => new Set(data?.progress.completedSteps ?? []),
    [data?.progress.completedSteps]
  );
  const { total, done } = useMemo(
    () => computeProgress(def, ctx?.autoDerived, data?.progress.completedSteps ?? []),
    [def, ctx, data?.progress.completedSteps]
  );

  // Stamp the guide complete once every step is done.
  const completedFired = useRef(false);
  useEffect(() => {
    if (!data) return;
    if (total > 0 && done >= total && !data.progress.completedAt && !completedFired.current) {
      completedFired.current = true;
      markStep.mutate({ completed: true });
    }
  }, [data, total, done, markStep]);

  if (!open) return null;

  const chapter = chapters[Math.min(stepIndex, chapters.length - 1)];
  const isLast = stepIndex >= chapters.length - 1;
  const sections = ctx
    ? chapter.sectionIds
        .map((id) => def.sections.find((s) => s.id === id))
        .filter((s): s is NonNullable<typeof s> => Boolean(s))
    : [];

  const footer = (
    <div className={styles.wizardFooter}>
      <button
        className={styles.wizardBack}
        onClick={() => setStepIndex((i) => Math.max(0, i - 1))}
        disabled={stepIndex === 0}
      >
        <i className="fa-solid fa-arrow-left" aria-hidden="true" /> {t('guide.wizard.back')}
      </button>
      <div className={styles.wizardDots} aria-hidden="true">
        {chapters.map((c, i) => (
          <span key={c.id} className={`${styles.dot} ${i === stepIndex ? styles.dotActive : ''}`} />
        ))}
      </div>
      {isLast ? (
        <button className={styles.wizardNext} onClick={closeGuide}>
          {t('guide.wizard.finish')} <i className="fa-solid fa-check" aria-hidden="true" />
        </button>
      ) : (
        <button className={styles.wizardNext} onClick={() => setStepIndex((i) => i + 1)}>
          {t('guide.wizard.next')} <i className="fa-solid fa-arrow-right" aria-hidden="true" />
        </button>
      )}
    </div>
  );

  return (
    <Modal open={open} onClose={closeGuide} title={t(`guide.${guideRole}.title`)} size="lg" footer={footer}>
      {isLoading ? (
        <div className={styles.centered}><Spinner /></div>
      ) : isError || !data || !ctx ? (
        <EmptyState icon="fa-triangle-exclamation" title={t('guide.ui.errorTitle')} description={t('guide.ui.errorBody')} />
      ) : (
        <div className={styles.wizardBody}>
          <div className={styles.wizardHead}>
            <span className={styles.wizardStepLabel}>
              {t('guide.wizard.stepOf', { current: stepIndex + 1, total: chapters.length })}
            </span>
            <h2 className={styles.wizardChapterTitle}>{t(chapter.titleKey)}</h2>
            <div className={styles.progressWrap}>
              <div className={styles.progressTrack}>
                <div
                  className={styles.progressFill}
                  style={{ width: `${total > 0 ? Math.round((done / total) * 100) : 0}%` }}
                />
              </div>
              <span className={styles.progressLabel}>{t('guide.ui.progress', { done, total })}</span>
            </div>
          </div>

          {sections.map((section) => (
            <GuideSectionView
              key={section.id}
              section={section}
              ctx={ctx}
              guideRole={guideRole}
              completedSet={completedSet}
              employerDocs={EMPLOYER_DOCS}
              employeeDocs={EMPLOYEE_DOCS}
              onToggleSteps={async (ids, next) => {
                // Sequential awaits so each optimistic cache patch sees the previous one —
                // firing them in parallel would race and drop a step (a section can carry
                // more than one manual acknowledgement).
                for (const id of ids) await markStep.mutateAsync({ stepId: id, done: next });
              }}
            />
          ))}

          {isLast && <p className={styles.disclaimer}>{t('guide.ui.disclaimer')}</p>}
        </div>
      )}
    </Modal>
  );
}
